// DateTimePicker.jsx
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

dayjs.locale('ko');

// ─── PickerWheel ───────────────────────────────────────────────────────────
/**
 * @param {string[]} items           - 표시할 항목 배열
 * @param {number}   defaultIndex    - 초기 선택 인덱스 (기본값 0)
 * @param {function} onChange        - 선택 변경 콜백 (item: string, index: number)
 * @param {number}   width           - 휠 너비(px) (기본값 80)
 * @param {Set<number>} disabledSet  - 선택 불가 인덱스 집합 (선택)
 */
export function PickerWheel({
  items,
  defaultIndex = 0,
  onChange,
  width = 80,
  disabledSet,
}) {
  const ITEM_HEIGHT = 40;

  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [transform, setTransform] = useState(0);
  const wheelRef = useRef(null);
  const containerRef = useRef(null);
  /** touchmove 직후 touchend에서 state transform이 아직 커밋되지 않는 경우 스냅 오류 방지 */
  const dragTransformRef = useRef(0);

  const updatePosition = useCallback(
    (index) => {
      const y = -index * ITEM_HEIGHT;
      dragTransformRef.current = y;
      setTransform(y);
      setCurrentIndex(index);
      onChange?.(items[index], index);
    },
    [items, onChange]
  );

  const handleStart = (e) => {
    dragTransformRef.current = transform;
    wheelRef.current.startTransform = transform;
    setIsDragging(true);
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    wheelRef.current.startY = clientY;
  };

  const handleMove = useCallback(
    (e) => {
      if (!isDragging) return;
      if (e.type === 'touchmove' && e.cancelable) {
        e.preventDefault();
      }
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const next =
        wheelRef.current.startTransform + (clientY - wheelRef.current.startY);
      dragTransformRef.current = next;
      setTransform(next);
    },
    [isDragging]
  );

  const handleEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const t = dragTransformRef.current;
    let index = Math.round(-t / ITEM_HEIGHT);
    index = Math.max(0, Math.min(items.length - 1, index));
    // disabled 항목이면 가장 가까운 활성 항목으로 clamp
    if (disabledSet?.has(index)) {
      let lo = index - 1;
      let hi = index + 1;
      while (lo >= 0 || hi < items.length) {
        if (lo >= 0 && !disabledSet.has(lo)) {
          index = lo;
          break;
        }
        if (hi < items.length && !disabledSet.has(hi)) {
          index = hi;
          break;
        }
        lo--;
        hi++;
      }
    }
    updatePosition(index);
  }, [isDragging, items.length, updatePosition, disabledSet]);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    updatePosition(defaultIndex);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className="relative touch-none overflow-hidden overscroll-contain rounded-xl bg-slate-50"
      style={{ width, height: '160px' }}
    >
      <div
        ref={wheelRef}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        style={{
          transform: `translateY(${transform}px)`,
          transition: isDragging
            ? 'none'
            : 'transform 0.3s cubic-bezier(0.17, 0.04, 0.03, 0.94)',
          padding: '60px 0',
        }}
      >
        {items.map((item, i) => {
          const isDisabled = disabledSet?.has(i);
          return (
            <div
              key={i}
              className={`h-10 text-center leading-10 transition-all select-none ${
                i === currentIndex
                  ? isDisabled
                    ? 'text-lg font-black text-slate-300'
                    : 'text-primary text-lg font-black'
                  : isDisabled
                    ? 'text-xs text-slate-200'
                    : 'text-sm text-slate-400'
              }`}
            >
              {item}
            </div>
          );
        })}
      </div>
      <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-slate-50 via-transparent to-slate-50" />
      <div className="bg-primary/5 pointer-events-none absolute top-1/2 right-0 left-0 h-10 -translate-y-1/2 border-y border-slate-200" />
    </div>
  );
}

/**
 * @param {{ hour: string, minute: string, period: string }} value
 * @param {function} onChange   - "HH:mm AM|PM" 형태 문자열 콜백
 * @param {string|null} minTime - 최솟값 "HH:mm" 24시간제 (선택). 이 시간 이전 선택 불가.
 * @param {string|null} maxTime - 최댓값 "HH:mm" 24시간제 (선택). 이 시간 이후 선택 불가.
 *
 * 동작 방식:
 *  - minTime/maxTime 범위를 벗어나는 (period, hour, minute) 조합을 흐리게 처리.
 *  - 드래그 끝에 범위 밖 항목이 선택되면 경계값으로 자동 clamp.
 *  - AM/PM 전환 시에도 전체 범위를 재계산해 반영.
 */
export function TimePicker({
  value,
  onChange,
  minTime = null,
  maxTime = null,
}) {
  const hours = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, '0')
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, '0')
  );
  const periods = ['AM', 'PM'];

  // 현재 선택값을 분(minutes) 단위 절댓값으로 변환
  const toAbsMinutes = (h, m, p) => {
    let hour = parseInt(h, 10);
    if (p === 'PM' && hour !== 12) hour += 12;
    if (p === 'AM' && hour === 12) hour = 0;
    return hour * 60 + parseInt(m, 10);
  };

  const minAbs = useMemo(() => {
    if (!minTime) return null;
    const [hh, mm] = minTime.split(':').map(Number);
    return hh * 60 + mm;
  }, [minTime]);

  const maxAbs = useMemo(() => {
    if (!maxTime) return null;
    const [hh, mm] = maxTime.split(':').map(Number);
    return hh * 60 + mm;
  }, [maxTime]);

  // period 휠: 해당 AM/PM 구간이 완전히 범위 밖이면 disabled
  const periodDisabledSet = useMemo(() => {
    const s = new Set();
    periods.forEach((p, i) => {
      // AM = 0~719분, PM = 720~1439분
      const segStart = p === 'AM' ? 0 : 720;
      const segEnd = p === 'AM' ? 719 : 1439;
      if (minAbs != null && segEnd < minAbs) s.add(i);
      if (maxAbs != null && segStart > maxAbs) s.add(i);
    });
    return s;
  }, [minAbs, maxAbs]);

  // hour 휠: 현재 period 기준으로 각 hour가 범위 밖이면 disabled
  const hourDisabledSet = useMemo(() => {
    const s = new Set();
    hours.forEach((h, i) => {
      const absMin = toAbsMinutes(h, '00', value.period);
      const absMax = toAbsMinutes(h, '59', value.period);
      if (minAbs != null && absMax < minAbs) s.add(i);
      if (maxAbs != null && absMin > maxAbs) s.add(i);
    });
    return s;
  }, [minAbs, maxAbs, value.period]);

  // minute 휠: 현재 period + hour 기준으로 범위 밖 분 disabled
  const minuteDisabledSet = useMemo(() => {
    const s = new Set();
    minutes.forEach((m, i) => {
      const abs = toAbsMinutes(value.hour, m, value.period);
      if (minAbs != null && abs < minAbs) s.add(i);
      if (maxAbs != null && abs > maxAbs) s.add(i);
    });
    return s;
  }, [minAbs, maxAbs, value.hour, value.period]);

  const handleTimeChange = (type, val) => {
    const updated = { ...value, [type]: val };

    // clamp: 변경 후 전체 값이 범위를 벗어나면 경계로 조정
    let absVal = toAbsMinutes(updated.hour, updated.minute, updated.period);
    if (minAbs != null && absVal < minAbs) {
      // minTime 으로 강제 설정
      const minD = dayjs().startOf('day').add(minAbs, 'minute');
      updated.hour = minD.format('hh');
      updated.minute = minD.format('mm');
      updated.period = minD.hour() >= 12 ? 'PM' : 'AM';
    } else if (maxAbs != null && absVal > maxAbs) {
      const maxD = dayjs().startOf('day').add(maxAbs, 'minute');
      updated.hour = maxD.format('hh');
      updated.minute = maxD.format('mm');
      updated.period = maxD.hour() >= 12 ? 'PM' : 'AM';
    }

    onChange(`${updated.hour}:${updated.minute} ${updated.period}`);
  };

  return (
    <div className="flex items-center justify-center gap-3 overscroll-contain rounded-2xl bg-white p-4 shadow-inner">
      <PickerWheel
        items={hours}
        defaultIndex={hours.indexOf(value.hour)}
        onChange={(v) => handleTimeChange('hour', v)}
        width={60}
        disabledSet={hourDisabledSet}
      />
      <span className="text-primary font-black">:</span>
      <PickerWheel
        items={minutes}
        defaultIndex={minutes.indexOf(value.minute)}
        onChange={(v) => handleTimeChange('minute', v)}
        width={60}
        disabledSet={minuteDisabledSet}
      />
      <PickerWheel
        items={periods}
        defaultIndex={periods.indexOf(value.period)}
        onChange={(v) => handleTimeChange('period', v)}
        width={56}
        disabledSet={periodDisabledSet}
      />
    </div>
  );
}

/**
 * @param {string}      value    - 선택된 날짜 "YYYY-MM-DD"
 * @param {function}    onChange - 날짜 선택 콜백 (date: "YYYY-MM-DD")
 * @param {string|null} minDate  - 선택 불가 최솟값 "YYYY-MM-DD" (선택)
 */
export function CustomCalendar({ value, onChange, minDate }) {
  const [viewDate, setViewDate] = useState(dayjs(value || undefined));

  const currentMonth = viewDate.startOf('month');
  const daysInMonth = currentMonth.daysInMonth();
  const startDay = currentMonth.day();

  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(currentMonth.date(i));

  return (
    <div className="animate-in fade-in slide-in-from-top-2 rounded-2xl border border-slate-100 bg-white p-4 shadow-inner duration-300">
      <div className="mb-4 flex items-center justify-between px-2">
        <span className="text-sm font-black text-slate-800">
          {viewDate.format('YYYY년 MM월')}
        </span>
        <div className="flex gap-1">
          <button
            onClick={() => setViewDate(viewDate.subtract(1, 'month'))}
            className="rounded-lg p-1 hover:bg-slate-100"
          >
            <span className="material-symbols-outlined text-sm">
              chevron_left
            </span>
          </button>
          <button
            onClick={() => setViewDate(viewDate.add(1, 'month'))}
            className="rounded-lg p-1 hover:bg-slate-100"
          >
            <span className="material-symbols-outlined text-sm">
              chevron_right
            </span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
          <div key={d} className="text-2xs py-1 font-bold text-slate-300">
            {d}
          </div>
        ))}
        {days.map((date, idx) => (
          <div
            key={idx}
            className="flex aspect-square items-center justify-center"
          >
            {date ? (
              <button
                disabled={
                  minDate != null && date.isBefore(dayjs(minDate), 'day')
                }
                onClick={() => onChange(date.format('YYYY-MM-DD'))}
                className={`h-8 w-8 rounded-xl text-xs font-bold transition-all disabled:cursor-not-allowed disabled:text-slate-300 ${
                  date.format('YYYY-MM-DD') === value
                    ? 'bg-primary text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {date.date()}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function getTimePickerValue(dateTimeStr) {
  const time = dateTimeStr?.split(' ')[1] || '09:00';
  const m = dayjs(`2024-01-01 ${time}`);
  return {
    hour: m.format('hh'),
    minute: m.format('mm'),
    period: m.hour() >= 12 ? 'PM' : 'AM',
  };
}
